import React, { Suspense, useEffect, useRef } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { ChakraProvider } from "@chakra-ui/react";
import "./App.css";
import "./styles/media-query.css";
import { useCampaignAttribution } from "./hooks/useCampaignAttribution";
import { availableGenres } from "./helpers/helpers";
import { supabase } from "./supabaseClient";
import posthog from "posthog-js";
import { trackLandingAttributed } from "./utils/posthogEvents";

// ─── i18n initialization ────────────────────────────────────────────────────────
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import translations from "./translations.json";

// ─── Core & layout imports ────────────────────────────────────────────────────────
import ScrollToTop from "./helpers/ScrollTop";
import { useAppInit } from "./hooks/useAppInit";
import { CookieConsentProvider } from "./hooks/useCookieConsent";
import CookieBanner from "./components/CookieBanner";
import PartnerCookieNotice from "./components/Partner/landing/PartnerCookieNotice";
import AppDownloadBanner from "./components/AppDownloadBanner";
import MarketingPopup from "./components/MarketingPopup";
import MascotBoundary from "./components/mascot/MascotBoundary";
import ProcessInvite from "./pages/ProcessInvite";
import Logo from "./components/Logo";
import MobileMenu from "./components/MobileMenu";
import useWidth from "./hooks/useWidth";
import { recordHomeInterestFromPath } from "./utils/homePersonalization";

// ─── Redux ──────────────────────────────────────────────────────────────────────
import { useDispatch, useSelector } from "react-redux";
import { changeToken, selectUser } from "./slices/userReducer";

// ─── Routes config ──────────────────────────────────────────────────────────────
import { AuthRoutes } from "./routes/AuthRoutes";
import { PublicRoutes } from "./routes/PublicRoutes";
import { AdminRoutes } from "./routes/AdminRoutes";
import { PartnerRoutes } from "./routes/PartnerRoutes";
import { AppRoutes } from "./routes/AppRoutes";
import { lazyWithRetry } from "./routes/lazyWithRetry";

// ─── Lazy imports for non-extracted components ─────────────────────────────────
const SeasonalThemeLayer = lazyWithRetry(() => import("./components/SeasonalThemeLayer"));
const Mascot = lazyWithRetry(() => import("./components/mascot/Mascot"));
const Homepage = lazyWithRetry(() => import("./pages/home/Homepage"));
const AuthorizedLayout = lazyWithRetry(() => import("./layout/AuthorizedLayout"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

const savedLanguage = localStorage.getItem("language") || navigator.language.split("-")[0];
i18n.use(initReactI18next).init({
  resources: translations,
  lng: savedLanguage,
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
});

// Wrapper component that re-evaluates when user state changes
// This ensures the sidebar shows when a token is applied
function HomepageRouteWrapper({ setLanguage, language, deals, eventData, spinWinEvents }) {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const location = useLocation();

  // Check for token in URL and apply it if found
  useEffect(() => {
    const getTokenFromUrl = () => {
      const queryParams = new URLSearchParams(location.search);
      return queryParams.get("token");
    };

    const urlToken = getTokenFromUrl();
    if (urlToken && !user.token) {
      dispatch(changeToken(urlToken));
    }
  }, [location.search, dispatch, user.token]);

  const getTokenFromUrl = () => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get("token");
  };

  const urlToken = getTokenFromUrl();
  let storedToken = null;
  if (typeof window !== 'undefined') {
    try {
      storedToken = localStorage.getItem("token");
    } catch (e) {}
  }

  let storedPromoterRef = null;
  if (typeof window !== 'undefined') {
    try {
      storedPromoterRef = localStorage.getItem("promoter_ref");
    } catch (e) {}
  }

  const hasToken = !!(user.token || urlToken || storedToken);
  const hasPromoterRef = !!(user.promoterRef || storedPromoterRef);
  const isLoggedIn = !!(user.id || hasToken || hasPromoterRef);
  const { width, breakpoints } = useWidth();

  if (isLoggedIn) {
    return (
      <AuthorizedLayout setLanguage={setLanguage} language={language}>
        <Homepage deals={deals} eventData={eventData} spinWinEvents={spinWinEvents} />
      </AuthorizedLayout>
    );
  }

  return (
    <>
      {width < breakpoints.lg && <MobileMenu />}
      <Homepage deals={deals} eventData={eventData} spinWinEvents={spinWinEvents} />
    </>
  );
}

function App() {
  useCampaignAttribution();
  const initData = useAppInit();
  const {
    eventsLoading,
    setEventsLoading,
    isLoading,
    eventData,
    spinWinEvents,
    prefetchedTokenFilters,
    deals,
    dealsLoading,
    language,
    setLanguage,
    user,
    location
  } = initData;

  // Web heartbeat — fire-and-forget, throttled to once per 60s
  const lastHeartbeatRef = useRef(0);

  useEffect(() => {
    const sendHeartbeat = () => {
      const now = Date.now();
      if (now - lastHeartbeatRef.current < 60000) return;
      lastHeartbeatRef.current = now;

      supabase.auth
        .getSession()
        .then(({ data }) => {
          const session = data?.session;
          const userId = session?.user?.id;
          if (!userId) return;
          return supabase
            .from("User")
            .update({
              last_active_at: new Date().toISOString(),
              last_active_platform: "web",
            })
            .eq("auth_user_id", userId);
        })
        .catch(() => {});
    };

    const onFocus = () => sendHeartbeat();

    sendHeartbeat();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  useEffect(() => {
    recordHomeInterestFromPath(location?.pathname || "");
  }, [location?.pathname]);

  // PostHog: anchor the session's traffic source (docs/analytics-events.md
  // §2.5). Fires once per browser session; prefers live URL params, falls
  // back to PostHog's own stored properties, then document.referrer.
  useEffect(() => {
    try {
      if (sessionStorage.getItem("ph_landing_attributed")) return;
      sessionStorage.setItem("ph_landing_attributed", "1");

      const params = new URLSearchParams(window.location.search);
      const first = (...values) => values.find((v) => v) || null;
      let referrerDomain = null;
      if (document.referrer) {
        try {
          referrerDomain = new URL(document.referrer).hostname;
        } catch { /* ignore */ }
      }

      trackLandingAttributed({
        utmSource: first(params.get("utm_source"), posthog.get_property("utm_source"), posthog.get_property("$initial_utm_source")),
        utmMedium: first(params.get("utm_medium"), posthog.get_property("utm_medium"), posthog.get_property("$initial_utm_medium")),
        utmCampaign: first(params.get("utm_campaign"), posthog.get_property("utm_campaign"), posthog.get_property("$initial_utm_campaign")),
        referringDomain: first(posthog.get_property("$referring_domain"), referrerDomain),
        landingPath: window.location.pathname,
      });
    } catch {
      // sessionStorage unavailable — analytics must never break the app
    }
  }, []);



  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center hero-section">
        <Logo />
      </div>
    );
  }

  const PageLoader = () => (
    <div className="h-screen w-screen flex items-center justify-center hero-section">
      <Logo />
    </div>
  );

  const genres = availableGenres;

  const isAdminOrPartnerRoute = location?.pathname?.startsWith('/admin') || location?.pathname?.startsWith('/partner-dashboard');
  const isPartnerLanding = /^\/partners\/?$/i.test(location?.pathname || '');

  return (
    <CookieConsentProvider>
      <>
        {!isAdminOrPartnerRoute && <ProcessInvite />}
        <ChakraProvider theme={"dark"}>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {PublicRoutes()}
              {AuthRoutes({ setLanguage })}
              
              <Route path="/" element={<HomepageRouteWrapper setLanguage={setLanguage} language={language} deals={deals} eventData={eventData} spinWinEvents={spinWinEvents} />} />
              
              {AppRoutes({
                setLanguage,
                language,
                eventData,
                spinWinEvents,
                eventsLoading,
                setEventsLoading,
                prefetchedTokenFilters,
                deals,
                dealsLoading,
                genres,
                user
              })}
              
              {AdminRoutes()}
              {PartnerRoutes()}
              
              <Route path="*" element={<NotFound setLanguage={setLanguage} language={language} />} />
            </Routes>
          </Suspense>
          {/* Seasonal theme for the member site and app. Suppresses itself on
              /admin, /partner-dashboard, /checkout and /thanks, and renders
              nothing when no theme is live. Mounted here rather than in a
              layout because the guest home page renders outside the layout.
              The public partner landing keeps its own business-focused design. */}
          {!isAdminOrPartnerRoute && !isPartnerLanding && (
            <Suspense fallback={null}>
              <SeasonalThemeLayer />
            </Suspense>
          )}
          {!isAdminOrPartnerRoute && !isPartnerLanding && (
            <MascotBoundary>
              <Suspense fallback={null}>
                <Mascot />
              </Suspense>
            </MascotBoundary>
          )}
          {!isAdminOrPartnerRoute && !isPartnerLanding && <AppDownloadBanner />}
          {isPartnerLanding ? <PartnerCookieNotice /> : <CookieBanner />}
          {!isAdminOrPartnerRoute && !isPartnerLanding && <MarketingPopup />}
        </ChakraProvider>
      </>
    </CookieConsentProvider>
  );
}

export default App;
